import axios from 'axios';
import { apiHost, config } from './request';

// T088 / ARCH-330 — the ONE async order-PDF client. Owns every network + polling concern for the
// worker-only generated-PDF routes. Callers (dashboards, form previews, the sign-contract page) never
// hit the backend route directly and never window.open a protected route — they go through here.
//
// Contract (backend orderPdfAccess, ?redirect=false):
//   200 { status:'AVAILABLE', url }              → open/return the presigned URL
//   202 { status:'PREPARING', stage:'ENRICHMENT' }         → legacy VUL self-heal; NO jobId yet
//   202 { jobId, status:'PENDING', artifact, contentHash } → PDF_ONLY enqueued (or an active reused job)
//   202 { jobId, status:'FAILED', retryable:true, artifact, contentHash } → a reused job is already
//     terminal (FAILED/DLQ/AMBIGUOUS/unexpected-DONE) — never treated as PENDING.
//   409 { code:'PDF_ARTIFACT_NOT_READY' }        → NOT_READY (e.g. VUL enrichment not recoverable)
//   other 4xx/5xx                                → FAILED (user-safe)
//
// Job-status polling (ARCH-330 recovery fix): once a jobId is known for an INTERNAL (non-signing)
// request, every subsequent poll targets GET /orders/:id/pdf-jobs/:jobId/status instead of
// re-calling the enqueue/access route — the SAME endpoint is used whether the jobId came from the
// initial enqueue or from POST /orders/:id/pdf-jobs/:jobId/retry. Signing-token requests never
// switch (that endpoint is Bearer-only via checkToken) and keep polling the original route, exactly
// as before.
//
// Two auth contexts:
//   internal  → Bearer via the shared request `config()` headers.
//   signing   → ?token=<signing JWT>, NO Authorization header (backend takes the signing branch).

export const ORDER_PDF_STATUS = Object.freeze({
  IDLE: 'IDLE',
  PREPARING: 'PREPARING',
  READY: 'READY',
  FAILED: 'FAILED',
  NOT_READY: 'NOT_READY',
  AMBIGUOUS: 'AMBIGUOUS'
});

const DEFAULT_POLL_BASE_MS = 1500;
const DEFAULT_POLL_MAX_MS = 5000;
const DEFAULT_MAX_ATTEMPTS = 40; // ~ up to a few minutes of bounded polling, then FAILED

// Raw pdf_email_jobs statuses (from GET/POST /pdf-jobs/:jobId/status|retry) — never the collapsed
// client-facing ORDER_PDF_STATUS labels.
const JOB_STATUS_TERMINAL_FAILED = Object.freeze([
  'FAILED',
  'DLQ',
  'AMBIGUOUS'
]);
const JOB_STATUS_ACTIVE = Object.freeze([
  'PENDING',
  'PROCESSING',
  'SENDING',
  'RETRYING'
]);

// Normalize a caller-supplied route (full URL or path, with/without an existing ?lang) into the
// apiHost-relative request URL, always forcing redirect=false and attaching the signing token when
// present. Preserves every pre-existing query param (e.g. lang/locale).
function buildRequestUrl(route, token) {
  let rel = String(route || '');
  if (rel.indexOf(apiHost) === 0) rel = rel.slice(apiHost.length);
  if (rel.charAt(0) !== '/') rel = `/${rel}`;
  const qIndex = rel.indexOf('?');
  const base = qIndex === -1 ? rel : rel.slice(0, qIndex);
  const existing = qIndex === -1 ? '' : rel.slice(qIndex + 1);
  const params = new URLSearchParams(existing);
  params.set('redirect', 'false');
  if (token) params.set('token', token);
  const query = params.toString();
  return { base, query };
}

// Stable identity for de-duplicating concurrent requests for the same logical artifact view.
function makeIdentity(orderId, base, query, token) {
  const params = new URLSearchParams(query);
  params.delete('token'); // token value is not part of the artifact identity
  const lang = params.get('lang') || params.get('language') || '';
  return `${orderId || ''}|${base}|${token ? 'signing' : 'internal'}|${lang}`;
}

function openInNewTab(url) {
  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// Browsers only allow window.open inside the click that triggered it; by the time polling finishes
// that gesture has expired and the tab is blocked. Callers open this placeholder synchronously in the
// click handler and hand it to requestOrderPdf, which navigates it to the PDF (or closes it on failure).
export function openPdfPlaceholderWindow() {
  if (typeof window === 'undefined' || typeof window.open !== 'function')
    return null;
  const popup = window.open('', '_blank');
  if (!popup) return null;
  try {
    popup.opener = null;
    popup.document.title = 'PDF';
    popup.document.body.textContent = 'Preparing PDF…';
  } catch (e) {
    /* placeholder text is cosmetic only */
  }
  return popup;
}

function closePlaceholder(sub) {
  const { popup } = sub;
  // eslint-disable-next-line no-param-reassign
  sub.popup = null;
  try {
    if (popup && !popup.closed) popup.close();
  } catch (e) {
    /* already gone */
  }
}

// sessionStorage persistence: pdfjob:{orderId}:{routePath} -> { jobId, contentHash }. Lets a page
// refresh/reopen (within the same tab session) resume polling a RETRY job (e.g. B) that the
// content-keyed enqueue route can never discover on its own. Never trusted blindly — every read is
// re-validated against the current contentHash, and the server re-validates ownership on every call
// (a stale/foreign jobId is rejected and cleared, see STALE_JOB handling in poll()).
function storageKey(orderId, base) {
  return `pdfjob:${orderId || ''}:${base}`;
}
function readPersistedJob(key) {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.jobId !== 'string') return null;
    return parsed;
  } catch (e) {
    return null;
  }
}
function writePersistedJob(key, { jobId, contentHash }) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(
      key,
      JSON.stringify({ jobId, contentHash: contentHash || null })
    );
  } catch (e) {
    /* storage unavailable/full — never break polling for this */
  }
}
function clearPersistedJob(key) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
}

// One network attempt against the enqueue/access route (initial view, or the fallback after a
// stale/foreign jobId is rejected). Never throws.
async function fetchArtifactOnce(fullUrl, token) {
  try {
    const res = token
      ? await axios.get(fullUrl) // signing: no Authorization header → backend token branch
      : await axios.get(fullUrl, config()); // internal: Bearer via shared config()
    const status = res && res.status;
    const data = (res && res.data) || {};
    if (status === 200 && data.status === 'AVAILABLE' && data.url) {
      return { kind: 'AVAILABLE', url: data.url };
    }
    if (status === 202) {
      // A reused (created:false) job that is already terminal is reported honestly — never a fake
      // PENDING that would poll a dead job forever.
      if (data.status === 'FAILED') {
        return {
          kind: 'FAILED',
          jobId: data.jobId,
          retryable: Boolean(data.retryable),
          contentHash: data.contentHash,
          artifact: data.artifact
        };
      }
      // Any other 202 is a NON-terminal preparation state. jobId is OPTIONAL: the legacy VUL
      // enrichment stage returns { status:'PREPARING', stage:'ENRICHMENT' } with NO jobId yet; a
      // later poll of the SAME route returns { status:'PENDING', jobId } once enrichment is READY.
      return {
        kind: 'PENDING',
        jobId: data.jobId,
        stage: data.stage,
        contentHash: data.contentHash,
        artifact: data.artifact
      };
    }
    // Interceptor resolves 403 as a value; anything else here is an unexpected/legacy shape.
    return { kind: 'FAILED' };
  } catch (err) {
    const httpStatus = err && err.response && err.response.status;
    const code =
      err && err.response && err.response.data && err.response.data.code;
    if (httpStatus === 409 || code === 'PDF_ARTIFACT_NOT_READY') {
      return { kind: 'NOT_READY' };
    }
    return { kind: 'FAILED' };
  }
}

// One network attempt against the job-scoped status endpoint. Used for every poll, INTERNAL context
// only, once a jobId is active — the SAME endpoint regardless of whether the jobId came from the
// initial enqueue or from a retry (Phase 6/7 of the recovery design).
async function fetchJobStatusOnce(orderId, jobId) {
  try {
    const res = await axios.get(
      `${apiHost}/orders/${orderId}/pdf-jobs/${jobId}/status`,
      config()
    );
    const data = (res && res.data) || {};
    const { status } = data;
    if (status === 'DONE') {
      const pdfUrl =
        data.artifacts && data.artifacts[0] && data.artifacts[0].pdfUrl;
      if (pdfUrl) return { kind: 'AVAILABLE', url: pdfUrl };
      // DONE but no resolvable URL yet (should normally be unreachable) — one more bounded tick,
      // never a hard failure just because the URL reconstruction raced the completion write.
      return { kind: 'PENDING' };
    }
    if (JOB_STATUS_TERMINAL_FAILED.includes(status)) {
      return { kind: 'FAILED', retryable: true };
    }
    if (JOB_STATUS_ACTIVE.includes(status)) {
      return { kind: 'PENDING' };
    }
    return { kind: 'FAILED' };
  } catch (err) {
    const httpStatus = err && err.response && err.response.status;
    if (httpStatus === 404 || httpStatus === 403) {
      // The active jobId is no longer valid server-side (wrong order/environment, or the row is
      // gone) — never trust it further. The caller must clear it and fall back to the enqueue route.
      return { kind: 'STALE_JOB' };
    }
    return { kind: 'FAILED' };
  }
}

// POST /orders/:id/pdf-jobs/:jobId/retry — job-scoped: the parent is EXACTLY the supplied jobId,
// never re-derived from order/artifact/contentHash. Never throws; normalized like the other fetchers.
async function postRetryOnce(orderId, jobId) {
  try {
    const res = await axios.post(
      `${apiHost}/orders/${orderId}/pdf-jobs/${jobId}/retry`,
      null,
      config()
    );
    const data = (res && res.data) || {};
    if (typeof data.jobId === 'string' && data.jobId) {
      return {
        kind: 'RETRIED',
        jobId: data.jobId,
        contentHash: data.contentHash
      };
    }
    return { kind: 'FAILED' };
  } catch (err) {
    const body = err && err.response && err.response.data;
    return { kind: 'FAILED', code: body && body.code };
  }
}

const activeRuns = new Map();

function createRun({
  identity,
  orderId,
  fullUrl,
  token,
  storageKeyName,
  pollIntervalMs,
  maxAttempts
}) {
  const subscribers = new Set();
  const maxTries = maxAttempts || DEFAULT_MAX_ATTEMPTS;
  let state = null; // {status, url?, jobId?, retryable?}
  let opened = false;
  let cancelled = false;
  let settled = false;
  let attempts = 0;
  let timer = null;
  // Once set (internal context only — signing-token requests never switch), every subsequent poll
  // targets the job-status endpoint instead of the enqueue/access route.
  let activeJobId = null;
  let retrying = false;
  let resolvePromise;
  let promise = new Promise(resolve => {
    resolvePromise = resolve;
  });

  const nextDelay = () => {
    if (pollIntervalMs) return pollIntervalMs;
    const grown = DEFAULT_POLL_BASE_MS * 1.5 ** (attempts - 1);
    return Math.min(Math.round(grown), DEFAULT_POLL_MAX_MS);
  };

  const emit = () => {
    if (!state) return;
    subscribers.forEach(sub => {
      if (typeof sub.onStatus === 'function') {
        try {
          sub.onStatus(state);
        } catch (e) {
          /* subscriber errors never break the run */
        }
      }
    });
  };

  const maybeOpen = () => {
    if (
      opened ||
      !state ||
      state.status !== ORDER_PDF_STATUS.READY ||
      !state.url
    )
      return;
    const wanting = Array.from(subscribers).filter(sub => sub.openPdf);
    if (wanting.length) {
      opened = true;
      const [first, ...rest] = wanting;
      if (first.popup && !first.popup.closed) {
        const { popup } = first;
        first.popup = null;
        try {
          popup.location.replace(state.url);
        } catch (e) {
          openInNewTab(state.url);
        }
      } else {
        openInNewTab(state.url);
      }
      rest.forEach(closePlaceholder);
    }
  };

  const settle = nextState => {
    if (settled || cancelled) return;
    settled = true;
    state = nextState;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    // A retryable terminal FAILED run stays addressable in activeRuns so retryOrderPdf() can find
    // it (via the SAME identity) and reactivate polling on a NEW jobId; every other terminal state
    // (READY/NOT_READY/non-retryable FAILED) is removed as before. cancel()/unsubscribe still
    // removes it once every subscriber is gone (see cancel() below).
    const keepAlive =
      nextState.status === ORDER_PDF_STATUS.FAILED && nextState.retryable;
    if (!keepAlive) activeRuns.delete(identity);
    if (nextState.status === ORDER_PDF_STATUS.READY)
      clearPersistedJob(storageKeyName);
    maybeOpen();
    if (nextState.status !== ORDER_PDF_STATUS.READY)
      subscribers.forEach(closePlaceholder);
    emit();
    resolvePromise(state);
  };

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    activeRuns.delete(identity);
    subscribers.forEach(closePlaceholder);
    if (!settled)
      resolvePromise({ status: ORDER_PDF_STATUS.IDLE, cancelled: true });
  };

  // A persisted jobId represents a LATER retry generation (e.g. B) that the content-keyed enqueue
  // route can never discover on its own (it always resolves back to the original terminal job A).
  // Only trusted when its stored contentHash still matches the identity the enqueue route just
  // reported — a content edit naturally invalidates it. Returns true when a persisted LATER
  // generation was substituted in place of the freshly-reported jobId.
  const resolveActiveJobId = (freshJobId, contentHash) => {
    if (token) return false; // signing context never switches to job-status polling
    const persisted = readPersistedJob(storageKeyName);
    if (
      persisted &&
      contentHash &&
      persisted.contentHash === contentHash &&
      persisted.jobId !== freshJobId
    ) {
      activeJobId = persisted.jobId;
      return true;
    }
    activeJobId = freshJobId || null;
    if (activeJobId)
      writePersistedJob(storageKeyName, { jobId: activeJobId, contentHash });
    return false;
  };

  const scheduleNext = () => {
    if (attempts >= maxTries) {
      settle({ status: ORDER_PDF_STATUS.FAILED });
      return;
    }
    // eslint-disable-next-line no-use-before-define
    timer = setTimeout(poll, nextDelay());
  };

  const poll = async () => {
    if (cancelled || settled) return;
    attempts += 1;
    const outcome = activeJobId
      ? await fetchJobStatusOnce(orderId, activeJobId)
      : await fetchArtifactOnce(fullUrl, token);
    if (cancelled || settled) return;

    if (outcome.kind === 'STALE_JOB') {
      clearPersistedJob(storageKeyName);
      activeJobId = null;
      state = { status: ORDER_PDF_STATUS.PREPARING };
      emit();
      scheduleNext();
      return;
    }
    if (outcome.kind === 'AVAILABLE') {
      settle({ status: ORDER_PDF_STATUS.READY, url: outcome.url });
      return;
    }
    if (outcome.kind === 'NOT_READY') {
      settle({ status: ORDER_PDF_STATUS.NOT_READY });
      return;
    }
    if (outcome.kind === 'FAILED') {
      if (!activeJobId && outcome.jobId) {
        const usingPersistedLaterGeneration = resolveActiveJobId(
          outcome.jobId,
          outcome.contentHash
        );
        if (usingPersistedLaterGeneration) {
          // The enqueue route only knows about the OLDER terminal job — a later retry generation
          // (persisted from before, e.g. a prior page load's retry) exists and must be polled for
          // its OWN real status instead of trusting stale FAILED info about the older job.
          state = { status: ORDER_PDF_STATUS.PREPARING, jobId: activeJobId };
          emit();
          scheduleNext();
          return;
        }
      }
      settle({
        status: ORDER_PDF_STATUS.FAILED,
        jobId: activeJobId,
        retryable: Boolean(outcome.retryable)
      });
      return;
    }
    // PENDING → keep the user informed and schedule the next bounded poll. `stage` (e.g. ENRICHMENT)
    // is preserved so the UI can distinguish "Preparing order data…" from "Preparing PDF…".
    if (!activeJobId && outcome.jobId)
      resolveActiveJobId(outcome.jobId, outcome.contentHash);
    state = {
      status: ORDER_PDF_STATUS.PREPARING,
      jobId: activeJobId || outcome.jobId,
      stage: outcome.stage
    };
    emit();
    scheduleNext();
  };

  // Retry the CURRENT active job (never the original — retry(B) must derive from B, not A). A
  // duplicate/concurrent click while a retry is already in flight is a no-op (the in-flight guard
  // below); the backend's own deterministic-per-jobId idempotency additionally collapses any
  // concurrent duplicate HTTP calls that do slip through to the same replacement job.
  const retry = async () => {
    // Only meaningful once the run has actually settled into a terminal, retryable FAILED state —
    // that is the ONLY reason a run stays addressable in activeRuns after settling (see settle()'s
    // keepAlive check above), so `settled` must be TRUE here, not falsy.
    if (retrying || cancelled || !settled || !activeJobId || !state) return;
    if (state.status !== ORDER_PDF_STATUS.FAILED || !state.retryable) return;
    retrying = true;
    // Leave the terminal state and hand out a FRESH deferred synchronously (before the POST even
    // goes out) — the original `promise` already resolved once (promises settle exactly once) and
    // must never be confused with this new retry cycle's eventual outcome; a caller invoking
    // requestOrderPdf() again (e.g. a second "Download" click) reads getPromise() right after this
    // call returns and must observe the NEW cycle, not the stale FAILED one.
    settled = false;
    promise = new Promise(resolve => {
      resolvePromise = resolve;
    });
    attempts = 0;
    state = { status: ORDER_PDF_STATUS.PREPARING, jobId: activeJobId };
    emit();
    try {
      const result = await postRetryOnce(orderId, activeJobId);
      if (cancelled) return;
      if (result.kind !== 'RETRIED') {
        settle({
          status: ORDER_PDF_STATUS.FAILED,
          jobId: activeJobId,
          retryable: false
        });
        return;
      }
      activeJobId = result.jobId;
      writePersistedJob(storageKeyName, {
        jobId: result.jobId,
        contentHash: result.contentHash
      });
      state = { status: ORDER_PDF_STATUS.PREPARING, jobId: activeJobId };
      emit();
      // eslint-disable-next-line no-use-before-define
      poll();
    } finally {
      retrying = false;
    }
  };

  const addSubscriber = sub => {
    subscribers.add(sub);
    if (state) {
      if (sub.onStatus) {
        try {
          sub.onStatus(state);
        } catch (e) {
          /* subscriber errors never break the run */
        }
      }
      maybeOpen();
      // A reattach to an already-failed run has nothing coming — don't leave a blank tab behind.
      if (
        state.status !== ORDER_PDF_STATUS.READY &&
        state.status !== ORDER_PDF_STATUS.PREPARING
      ) {
        closePlaceholder(sub);
      }
    }
    return () => {
      subscribers.delete(sub);
      closePlaceholder(sub);
      if (subscribers.size === 0) cancel();
    };
  };

  return {
    getPromise: () => promise,
    addSubscriber,
    start: poll,
    retry,
    isRetryable: () =>
      Boolean(activeJobId) &&
      state &&
      state.status === ORDER_PDF_STATUS.FAILED &&
      state.retryable
  };
}

/**
 * Request (and, by default, open) a generated order PDF using the worker-only async contract.
 * @param {Object} params
 * @param {string|number} params.orderId
 * @param {string} params.route            PDF route (full URL or apiHost-relative path; may carry ?lang).
 * @param {string} [params.token]          Signing JWT — when present the request omits Bearer auth.
 * @param {Function} [params.onStatus]     Called with { status, url?, jobId?, retryable? } on each state change.
 * @param {AbortSignal} [params.signal]    Abort to detach this subscriber (cancels the run if it was the last).
 * @param {boolean} [params.openPdf=true]  When true, opens the presigned URL in a new tab exactly once.
 * @param {Window} [params.popup]          Placeholder tab opened synchronously in the click (see openPdfPlaceholderWindow).
 * @param {number} [params.pollIntervalMs] Fixed poll delay (defaults to bounded exponential; used by tests).
 * @param {number} [params.maxAttempts]
 * @returns {Promise<{status:string, url?:string}>}
 */
export function requestOrderPdf({
  orderId,
  route,
  token,
  onStatus,
  signal,
  openPdf = true,
  popup = null,
  pollIntervalMs,
  maxAttempts
} = {}) {
  const { base, query } = buildRequestUrl(route, token);
  const identity = makeIdentity(orderId, base, query, token);
  const fullUrl = `${apiHost}${base}?${query}`;
  const storageKeyName = storageKey(orderId, base);

  let run = activeRuns.get(identity);
  if (!run) {
    run = createRun({
      identity,
      orderId,
      fullUrl,
      token,
      storageKeyName,
      pollIntervalMs,
      maxAttempts
    });
    activeRuns.set(identity, run);
    run.start();
  } else if (run.isRetryable()) {
    // A run already settled terminal FAILED+retryable for this exact identity — a fresh call here
    // represents a NEW user action (e.g. clicking "Download" again) and must retry the failed job,
    // never silently reattach to the dead settled run (which would look like a no-op to the caller).
    run.retry();
  }

  const unsubscribe = run.addSubscriber({ onStatus, openPdf, popup });
  if (signal) {
    if (signal.aborted) unsubscribe();
    else signal.addEventListener('abort', unsubscribe, { once: true });
  }
  return run.getPromise();
}

/**
 * Retry the currently-active (terminal, retryable) job for an in-progress/settled orderPdf run.
 * Must be called against the SAME identity a prior requestOrderPdf({orderId, route, token}) used.
 * No-ops if there is no active run, no active jobId, or a retry is already in flight.
 * @returns {boolean} true if a retry request was actually issued.
 */
export function retryOrderPdf({ orderId, route, token } = {}) {
  const { base, query } = buildRequestUrl(route, token);
  const identity = makeIdentity(orderId, base, query, token);
  const run = activeRuns.get(identity);
  if (!run) return false;
  run.retry();
  return true;
}

export default requestOrderPdf;
